import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Grid, Stack, TextField, Typography,
} from '@mui/material';
import api from '../api/api';
import { abrirCaja, fetchCajaEstado } from '../api/restaurantApi';
import { formatMoney } from '../utils/format';
import PinConfirmModal from './PinConfirmModal';

const DENOMS = [20000, 10000, 5000, 2000, 1000, 500, 100, 50, 10];

function requiresCloseAmount(method) {
  const tipo = String(method?.tipo || '').toUpperCase();
  return method?.usa_terminal === true || (method?.requiere_liquidacion === true && ['TARJETA', 'PASARELA'].includes(tipo));
}

export default function CajaModal({ open, onClose, cajaState, onRefresh }) {
  const [initial, setInitial] = useState('');
  const [counts, setCounts] = useState({});
  const [methodTotals, setMethodTotals] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [detalle, setDetalle] = useState(cajaState?.caja || null);
  const [pinOpen, setPinOpen] = useState(false);

  useEffect(() => {
    if (!open) return;
    setError('');
    setInitial('');
    setCounts({});
    setMethodTotals({});
    setDetalle(cajaState?.caja || null);
    if (cajaState?.abierta) {
      fetchCajaEstado().then((state) => setDetalle(state.caja)).catch(() => {});
    }
  }, [open, cajaState]);

  const totalCash = useMemo(() => DENOMS.reduce((sum, value) => sum + value * Number(counts[value] || 0), 0), [counts]);
  const closeMethods = useMemo(() => {
    const rows = Array.isArray(detalle?.medios_pago) ? detalle.medios_pago : [];
    const dynamic = rows.filter((row) => Number(row?.total || 0) > 0 && requiresCloseAmount(row));
    const transbank = Number(detalle?.ventas_transbank ?? detalle?.saldo_teorico_transbank ?? 0);
    if (transbank > 0 && !dynamic.some((row) => String(row?.clave || '').toUpperCase() === 'TRANSBANK')) {
      dynamic.push({ clave: 'TRANSBANK', nombre: 'Transbank', total: transbank, tipo: 'TARJETA', usa_terminal: true });
    }
    return dynamic;
  }, [detalle]);

  const openCash = async () => {
    setLoading(true); setError('');
    try {
      await abrirCaja(initial);
      await onRefresh?.();
      onClose?.();
    } catch (err) {
      setError(err?.response?.data?.message || err?.response?.data?.error || err.message || 'No se pudo abrir la caja.');
    } finally { setLoading(false); }
  };

  const requestClose = () => {
    const missing = closeMethods.find((row) => String(methodTotals[row.clave] ?? '').trim() === '');
    if (missing) return setError(`Ingrese el total de cierre de ${missing.nombre || missing.clave}.`);
    setPinOpen(true);
  };

  const closeCash = async (pinData) => {
    setPinOpen(false);
    setLoading(true); setError('');
    try {
      const body = { monto_efectivo_real: totalCash, detalle_arqueo: counts };
      if (closeMethods.length) {
        body.confirmacion_medios = Object.fromEntries(closeMethods.map((row) => [row.clave, { monto_real: Number(methodTotals[row.clave] || 0), referencia: null }]));
      }
      await api.post(`/pos_cajas/${detalle?.id}/cerrar`, body, { headers: { 'X-PIN-Token': pinData.token } });
      await onRefresh?.();
      onClose?.();
    } catch (err) {
      setError(err?.response?.data?.message || err?.response?.data?.error || err.message || 'No se pudo cerrar la caja.');
    } finally { setLoading(false); }
  };

  return (
    <>
      <Dialog open={open} onClose={loading ? undefined : onClose} fullWidth maxWidth="md">
        <DialogTitle sx={{ fontWeight: 950 }}>{cajaState?.abierta ? `Caja #${detalle?.id || ''}` : 'Abrir caja'}</DialogTitle>
        <DialogContent dividers>
          {error ? <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert> : null}
          {!cajaState?.abierta ? (
            <TextField fullWidth label="Monto inicial" value={initial} onChange={(e) => setInitial(e.target.value.replace(/\D/g, ''))} helperText="Efectivo disponible al iniciar el turno" autoFocus />
          ) : (
            <Stack spacing={1.3}>
              <Alert severity="info">Cierre ciego: ingrese el conteo físico. Total contado: <strong>{formatMoney(totalCash)}</strong></Alert>
              <Grid container spacing={1}>
                {DENOMS.map((value) => (
                  <Grid item xs={6} sm={4} md={3} key={value}>
                    <TextField fullWidth size="small" label={formatMoney(value)} value={counts[value] || ''} onChange={(e) => setCounts((current) => ({ ...current, [value]: e.target.value.replace(/\D/g, '') }))} />
                  </Grid>
                ))}
              </Grid>
              {closeMethods.length ? (
                <Box>
                  <Typography fontWeight={950} sx={{ mb: 0.6 }}>Confirmación de otros medios</Typography>
                  <Grid container spacing={1}>
                    {closeMethods.map((row) => (
                      <Grid item xs={12} sm={6} key={row.clave}>
                        <TextField fullWidth label={`${row.nombre || row.clave} · total reportado`} value={methodTotals[row.clave] || ''} onChange={(e) => setMethodTotals((current) => ({ ...current, [row.clave]: e.target.value.replace(/\D/g, '') }))} />
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              ) : null}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={loading}>Cancelar</Button>
          {!cajaState?.abierta ? <Button variant="contained" onClick={openCash} disabled={loading}>{loading ? <CircularProgress size={18} color="inherit" /> : 'Abrir caja'}</Button> : <Button variant="contained" color="error" onClick={requestClose} disabled={loading}>Cerrar caja</Button>}
        </DialogActions>
      </Dialog>
      <PinConfirmModal open={pinOpen} onClose={() => setPinOpen(false)} onVerified={closeCash} />
    </>
  );
}
